if (!window.customElements.get('associated-variant-swatches')) {
  class AssociatedVariantSwatches extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('data-form-id');
      this.displayMode = this.getAttribute('data-display-mode') || 'swatches';
      this.onClick = this.onClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('click', this.onClick);
      document.addEventListener('variant:change', this.onVariantChange);

      if (this.displayMode === 'dropdown') {
        document.addEventListener('click', this.onDocumentClick);
        this.addEventListener('keydown', this.onKeydown);
      }

      this.syncSelectionFromDom();
    }

    // The script can be evaluated before this element's children are parsed, so the
    // trigger/panel are queried lazily (at click time) instead of cached on connect.
    get trigger() {
      return this.querySelector('[data-associated-variant-trigger]');
    }

    get panel() {
      return this.querySelector('[data-associated-variant-panel]');
    }

    get triggerMedia() {
      return this.querySelector('[data-associated-variant-trigger-media]');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('variant:change', this.onVariantChange);

      if (this.displayMode === 'dropdown') {
        document.removeEventListener('click', this.onDocumentClick);
        this.removeEventListener('keydown', this.onKeydown);
      }
    }

    onClick(event) {
      if (this.displayMode === 'dropdown') {
        const trigger = event.target.closest('[data-associated-variant-trigger]');

        if (trigger) {
          event.preventDefault();
          this.toggleDropdown();
          return;
        }
      }

      const swatchButton = event.target.closest('[data-associated-variant-swatch]');

      if (!swatchButton || swatchButton.disabled) {
        return;
      }

      event.preventDefault();
      this.selectVariantFromSwatch(swatchButton);

      if (this.displayMode === 'dropdown') {
        this.closeDropdown();
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeDropdown();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeDropdown();
        if (this.trigger) {
          this.trigger.focus();
        }
      }
    }

    toggleDropdown() {
      if (this.getAttribute('data-open') === 'true') {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    openDropdown() {
      if (!this.panel || !this.trigger) {
        return;
      }

      this.setAttribute('data-open', 'true');
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    closeDropdown() {
      if (!this.panel || !this.trigger) {
        return;
      }

      this.removeAttribute('data-open');
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    onVariantChange(event) {
      if (!event?.detail?.variant) {
        return;
      }

      if (event.detail.formId !== this.formId) {
        return;
      }

      this.setSelectedByVariantId(String(event.detail.variant.id));
    }

    syncSelectionFromDom() {
      const selected = this.querySelector('[data-associated-variant-swatch][data-selected="true"]');

      if (selected) {
        this.setSelectedByVariantId(selected.getAttribute('data-variant-id'));
      }
    }

    setSelectedByVariantId(variantId) {
      let selectedTitle = '';
      let selectedButton = null;

      this.querySelectorAll('[data-associated-variant-swatch]').forEach((button) => {
        const isSelected = button.getAttribute('data-variant-id') === variantId;
        button.classList.toggle('active', isSelected);
        button.setAttribute('data-selected', isSelected ? 'true' : 'false');

        if (this.displayMode === 'dropdown') {
          button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        } else {
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        }

        if (isSelected) {
          selectedButton = button;
          selectedTitle = button.getAttribute('data-variant-title') || button.getAttribute('aria-label') || '';
        }
      });

      this.updateSelectedLabel(selectedTitle);
      this.updateTriggerMedia(selectedButton);
    }

    updateSelectedLabel(title) {
      const label = this.querySelector('[data-associated-variant-selected]');

      if (label && title) {
        label.textContent = title;
      }
    }

    updateTriggerMedia(selectedButton) {
      if (this.displayMode !== 'dropdown' || !this.triggerMedia || !selectedButton) {
        return;
      }

      const media = selectedButton.querySelector('[data-associated-variant-media]');

      if (media) {
        this.triggerMedia.innerHTML = media.innerHTML;
      }
    }

    selectVariantFromSwatch(swatchButton) {
      const form = document.forms[this.formId];
      if (!form) {
        return;
      }

      const optionValueIds = [
        swatchButton.getAttribute('data-option-value-id-1'),
        swatchButton.getAttribute('data-option-value-id-2'),
        swatchButton.getAttribute('data-option-value-id-3')
      ];

      let changedInput = null;

      optionValueIds.forEach((optionValueId, index) => {
        const optionPosition = index + 1;

        if (!optionValueId) {
          return;
        }

        const escapedValue = window.CSS && window.CSS.escape ? window.CSS.escape(optionValueId) : optionValueId.replace(/"/g, '\\"');
        const input = document.querySelector(
          `input[data-option-position="${optionPosition}"][form="${this.formId}"][value="${escapedValue}"]`
        );

        if (!input) {
          return;
        }

        if (!input.checked) {
          input.checked = true;
          changedInput = input;
        }
      });

      if (changedInput) {
        changedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  window.customElements.define('associated-variant-swatches', AssociatedVariantSwatches);
}
